import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import {
  faTimes,
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LinearProgress from '@material-ui/core/LinearProgress';

import NkDialogTransition from '../nkDialogTransition';
import { createStyles, Theme } from '@material-ui/core';

const NkFormDialog = (props: any) => {
  const theme = useTheme();
  const styles = (_theme: Theme) =>
    createStyles({
      appBar: {
        backgroundColor: theme.customProps.header.backgroundColor,
        color: theme.palette.text.primary,
        padding: 0,
        textTransform: 'uppercase',
        boxShadow: 'none',
        borderBottom: theme.customProps.header.borderBottom,
        minHeight: 55,
        height: 55,
      },
      toolbar: {
        minHeight: 55,
        height: 55,
      },
      dialogContent: {
        padding: '10px 20px',
        minHeight: 180,
        '&.md': {
          minHeight: 280,
          minWidth: 360,
        },
        '&.lg': {
          minHeight: 320,
          minWidth: 400,
        },
        '&.xl': {
          minHeight: 400,
          minWidth: 600,
        },
        '&.xxl': {
          minHeight: 400,
          minWidth: 1200,
        },
        '&.xxxl': {
          minHeight: '85%',
          minWidth: '85%',
        },
      },
      closeButton: {
        position: 'absolute',
        color: theme.palette.text.primary,
        right: theme.spacing(),
        width: 48,
      },
    });

  const onClose = (event: Event, reason: string) => {
    if (reason !== 'backdropClick') {
      event.preventDefault();
      props.onClose(event);
    }
  };

  const useStyles = makeStyles(styles);
  const classes = useStyles();
  return (
    <Dialog
      open={props.isOpen}
      onClose={onClose}
      TransitionComponent={NkDialogTransition}
      disableEnforceFocus
      fullWidth={props.fullWidth}
      maxWidth={props.width ? props.width : 'sm'}
      onExited={props.onExited}
    >
      <AppBar
        position="relative"
        classes={{
          root: classes.appBar,
        }}
      >
        <Toolbar classes={{ regular: classes.toolbar }}>
          <Typography color="inherit" variant="h6">
            {props.title}
          </Typography>
          <IconButton
            color="inherit"
            className={classes.closeButton}
            onClick={props.onClose}
          >
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent className={`${classes.dialogContent} ${props.size}`}>
        {props.children}
        {props.isLoading && <LinearProgress />}
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={props.onSave}
          disabled={props.isLoading || props.saveDisabled}
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faCheckCircle} />}
        >
          {props.saveLabelText}
        </Button>
        <Button
          color="primary"
          onClick={props.onClose}
          disabled={props.isLoading}
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faTimesCircle} />}
        >
          {props.closeLabelText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NkFormDialog.propTypes = {
  title: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  size: PropTypes.oneOf(['md', 'lg', 'xl', 'xxl', 'xxxl']),
  width: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  fullWidth: PropTypes.bool,
  isLoading: PropTypes.bool,
  saveLabelText: PropTypes.string,
  closeLabelText: PropTypes.string,
  onExited: PropTypes.func,
  children: PropTypes.object,
};

export default NkFormDialog;
